import React, { memo, useCallback, useState } from "react";

//types
import { IRemovePlanMessage, PlanDetails } from "@swiggy-private/cart-types";
import { LOTTIE_ERROR_TYPES } from "@swiggy-private/core-types";

//dls
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Button } from "@swiggy-private/dls-web-core/dist/components/Button";

//styles
import {
    CTAContainer,
    HeaderContainer,
    LottieContainer,
    SecondaryCTATextButton,
    TextContainer,
    TitleTextContainer,
} from "./styles";

//constants
import { PLAN_REMOVAL_BOTTOMSHEET_DATA_TEST_IDS } from "@swiggy-private/cart-test-ids";

//internal deps
import { LottiePlayerComp } from "@super-ui/components/m2c-swiggy-one-halfcard/Components/PostState/components/LottiePlayer";
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";
import { getPrimaryCTA, getSecondaryCTA } from "./helpers";

interface IPlanRemoveConfirmationBottomsheetV2 {
    removePlanMessage: IRemovePlanMessage;
    primaryCTAHandler: VoidFunction;
    secondaryCTAHandler: VoidFunction;
    planDetails?: PlanDetails;
}
const PlanRemoveConfirmationBottomsheetV2 = ({
    removePlanMessage,
    primaryCTAHandler,
    secondaryCTAHandler,
    planDetails,
}: IPlanRemoveConfirmationBottomsheetV2) => {
    const { attribute_values: attributeValues, title, content } = removePlanMessage;
    const [showLottie, setShowLottie] = useState(true);

    const hasSignificantCartDifference =
        Number(attributeValues.plan_in_cart_savings) > Number(attributeValues.cart_total_diff_config);

    const onLottieError = useCallback((errorType?: LOTTIE_ERROR_TYPES, error?: unknown) => {
        setShowLottie(false);
        recordNREvent({
            eventType: NEWRELIC_TABLES.ONE_GRATIFICATION_HALF_CARD_EVENTS,
            errorType: String(errorType),
            error: String(error),
        });
    }, []);

    const primaryCTA = getPrimaryCTA(
        hasSignificantCartDifference,
        attributeValues.plan_in_cart_savings,
        planDetails?.type,
    );
    const secondaryCTA = getSecondaryCTA(hasSignificantCartDifference, planDetails?.type);

    const renderHeader = useCallback(
        () => (
            <HeaderContainer>
                <TitleTextContainer>
                    <Text
                        variant="V2.body1.neutral.regular"
                        color="text_Med_Emphasis"
                        data-testid={PLAN_REMOVAL_BOTTOMSHEET_DATA_TEST_IDS.HEADING}
                    >
                        {title.text}
                    </Text>
                </TitleTextContainer>

                {hasSignificantCartDifference ? (
                    <Text
                        variant="V2.heading1.neutral.black"
                        color="text_Highest_Emphasis"
                        textAlign="center"
                        data-testid={PLAN_REMOVAL_BOTTOMSHEET_DATA_TEST_IDS.CONTENT}
                    >
                        {content[0].title}
                    </Text>
                ) : (
                    <TextContainer>
                        <Text
                            variant="V2.heading1.neutral.black"
                            color="text_Highest_Emphasis"
                            textAlign="center"
                            data-testid={PLAN_REMOVAL_BOTTOMSHEET_DATA_TEST_IDS.CONTENT}
                        >
                            {content[0].title}
                        </Text>
                    </TextContainer>
                )}
            </HeaderContainer>
        ),
        [content, hasSignificantCartDifference, title.text],
    );

    const renderLottiePlayer = useCallback(
        () =>
            showLottie && (
                <LottieContainer>
                    <LottiePlayerComp
                        isPlanRemovalHalfCard
                        onError={onLottieError}
                        planRemovalHalfCardData={{
                            previousAmount: attributeValues.cart_total_plan_added,
                            updatedAmount: attributeValues.cart_total_plan_not_added,
                            planType: planDetails?.type,
                        }}
                    />
                </LottieContainer>
            ),
        [
            attributeValues.cart_total_plan_added,
            attributeValues.cart_total_plan_not_added,
            onLottieError,
            planDetails?.type,
            showLottie,
        ],
    );

    const renderCTAButtons = useCallback(
        () => (
            <CTAContainer>
                <Button
                    type="button"
                    variant="primary"
                    onClick={primaryCTAHandler}
                    data-testid={PLAN_REMOVAL_BOTTOMSHEET_DATA_TEST_IDS.PRIMARY_CTA}
                >
                    {primaryCTA}
                </Button>
                <SecondaryCTATextButton
                    buttonSize="medium"
                    type="button"
                    color="primary"
                    onClick={secondaryCTAHandler}
                    textAlign="center"
                    data-testid={PLAN_REMOVAL_BOTTOMSHEET_DATA_TEST_IDS.SECONDARY_CTA}
                >
                    {secondaryCTA}
                </SecondaryCTATextButton>
            </CTAContainer>
        ),
        [primaryCTA, primaryCTAHandler, secondaryCTA, secondaryCTAHandler],
    );

    return (
        <HalfCard isOpen={true} onClose={primaryCTAHandler}>
            {renderHeader()}
            {renderLottiePlayer()}
            {renderCTAButtons()}
        </HalfCard>
    );
};

export default memo(PlanRemoveConfirmationBottomsheetV2);
